import React, { useState, useEffect, useCallback } from 'react';
import '../Configuration.css';
import {
  Box, Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CommonGridHeader, { CommonDeleteButton, CommonEditButton, DeletePopup } from '../../../Components/Common Components/CommonComponents';
import { DataGrid } from '@mui/x-data-grid';
import { withTranslation } from 'react-i18next';
import { Colors } from '../../../Styles/Colors';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import { CheckAccessFunc } from '../../../Components/CommonFunctions/CommonFunctions';

const CHECKLIST_TYPE_OPTIONS = [
  { value: 'admission', label: 'Admission' },
  { value: 'discharge', label: 'Discharge' },
];

const emptyItem = () => ({ id: null, item_name: '' });

function ChecklistMaster() {
  const [checklistType, setChecklistType] = useState('admission');
  const [categoryTitle, setCategoryTitle] = useState('');
  const [itemRows, setItemRows] = useState([emptyItem()]);
  const [masterList, setMasterList] = useState([]);
  const [current_page, setCurrent_page] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoader, setIsLoader] = useState(false);
  const [disableBtn, setDisableBtn] = useState(false);
  const [showSuccessResponse, setShowSuccessResponse] = useState(false);
  const [showErrorResponse, setShowErrorResponse] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const editAccess = CheckAccessFunc('front_office', 'Settings', 'Hospital Configuration', 'Checklist Master', 'SubTab')?.editAccess;

  const fetchMasterList = useCallback(() => {
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_CHECKLIST_MASTER}?type=${checklistType}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setMasterList(Array.isArray(res.data?.data) ? res.data.data : []);
        } else setMasterList([]);
        setIsLoader(false);
      })
      .catch(() => { setMasterList([]); setIsLoader(false); });
  }, [checklistType]);

  useEffect(() => { fetchMasterList(); }, [fetchMasterList]);

  const addItemRow = () => setItemRows((prev) => [...prev, emptyItem()]);
  const removeItemRow = (index) => {
    setItemRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };
  const setItemRow = (index, value) => {
    setItemRows((prev) => prev.map((row, i) => (i === index ? { ...row, item_name: value } : row)));
  };

  const handleClear = () => {
    setCategoryTitle('');
    setItemRows([emptyItem()]);
    setSelectedRow(null);
  };

  const handleSave = () => {
    const title = (categoryTitle || '').trim();
    if (!title) {
      setShowErrorResponse(true);
      setResponseMessage('Enter category title.');
      return;
    }
    const items = itemRows.map((r) => (r.item_name || '').trim()).filter(Boolean);
    if (items.length === 0) {
      setShowErrorResponse(true);
      setResponseMessage('Add at least one checklist item.');
      return;
    }
    setDisableBtn(true);
    setIsLoader(true);
    const itemsPayload = itemRows
      .map((r) => (r.item_name || '').trim())
      .filter(Boolean)
      .map((name, order) => ({ id: null, item_name: name, order: order + 1 }));
    const payload = {
      id: selectedRow?.id ?? null,
      category_title: title,
      checklist_type: checklistType,
      order: selectedRow?.order ?? masterList.length + 1,
      items: selectedRow?.id
        ? itemRows
          .filter((r) => (r.item_name || '').trim())
          .map((row, order) => ({ id: row.id ?? null, item_name: (row.item_name || '').trim(), order: order + 1 }))
        : itemsPayload,
    };
    RestAPIService.create(payload, Serviceurls.IP_CHECKLIST_MASTER)
      .then((res) => {
        setIsLoader(false);
        setDisableBtn(false);
        if (res.data?.status === 'success') {
          setShowSuccessResponse(true);
          setResponseMessage(res.data?.message || 'Saved.');
          handleClear();
          fetchMasterList();
        } else {
          setShowErrorResponse(true);
          setResponseMessage(res.data?.message || 'Save failed.');
        }
      })
      .catch((err) => {
        setIsLoader(false);
        setDisableBtn(false);
        setShowErrorResponse(true);
        setResponseMessage(err?.response?.data?.message || err?.message || 'Save failed.');
      });
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setCategoryTitle(row.category_title || row.title || '');
    const items = row.items || [];
    setItemRows(items.length > 0 ? items.map((it) => ({ id: it.id, item_name: it.item_name || it.name || '' })) : [emptyItem()]);
  };

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!selectedRow?.id) { setDeleteOpen(false); return; }
    const deleteUrl = `${Serviceurls.IP_CHECKLIST_MASTER}?id=${selectedRow.id}`;
    RestAPIService.delete(deleteUrl)
      .then((res) => {
        if (res.data?.status === 'success') {
          setShowSuccessResponse(true);
          setResponseMessage(res.data?.message || 'Deleted.');
          setDeleteOpen(false);
          setSelectedRow(null);
          handleClear();
          fetchMasterList();
        } else {
          setShowErrorResponse(true);
          setResponseMessage(res.data?.message || 'Delete failed.');
        }
      })
      .catch((err) => {
        setShowErrorResponse(true);
        setResponseMessage(err?.response?.data?.message || err?.message || 'Delete failed.');
        setDeleteOpen(false);
      });
  };

  const msgClose = () => {
    setShowSuccessResponse(false);
    setShowErrorResponse(false);
    setResponseMessage('');
  };

  const flatRows = masterList.map((cat, idx) => ({
    ...cat,
    sno: idx + 1,
    item_count: Array.isArray(cat.items) ? cat.items.length : 0,
  }));

  const columns = [
    { field: 'sno', headerName: 'S.No', sortable: false, flex: 0.1, renderCell: (params) => params.row.sno },
    { field: 'category_title', headerName: 'Category Title', flex: 0.5, valueGetter: (v) => v.row.category_title || v.row.title || '-' },
    { field: 'item_count', headerName: 'Items', flex: 0.2 },
    ...(editAccess ? [{
      field: 'actions', headerName: 'Action', flex: 0.2, headerAlign: 'center', align: 'center', sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <CommonEditButton size="1.5vw" onClick={() => handleEdit(params.row)} />
          <CommonDeleteButton size="1.5vw" onClick={() => handleDeleteClick(params.row)} />
        </Stack>
      ),
    }] : []),
  ];

  return (
    <Box id="eMed_srvConfig_Container" sx={{ backgroundColor: Colors.configHomeBg }}>
      <Grid container spacing={3} className="eMed_srvConfig_Box">
        <Grid item xs={7.6}>
          <Paper className="eMed_srvConfig_Div">
            <DataGrid
              rows={flatRows}
              columns={columns}
              getRowId={(row) => row.id ?? row.sno}
              localeText={{ toolbarColumns: '', toolbarDensity: '', toolbarExport: '', toolbarFilters: '', toolbarExportPrint: '' }}
              disableRowSelectionOnClick
              components={{
                Toolbar: CommonGridHeader.CommonToolBar,
                NoRowsOverlay: () => (
                  <Stack direction="row" justifyContent="center" alignItems="center" sx={{ height: '53vh' }}>
                    No records found
                  </Stack>
                ),
              }}
              headerHeight={40}
              pageSizeOptions={[10, 20, 30]}
              pagination
              paginationMode="client"
              paginationModel={{ page: current_page, pageSize: rowsPerPage }}
              onPaginationModelChange={(model) => { setCurrent_page(model.page); setRowsPerPage(model.pageSize); }}
              loading={isLoader}
            />
          </Paper>
        </Grid>
        <Grid item xs={4.4}>
          <Paper className="eMed_srvConfig_Div">
            <div className="eMed_srvConfig_rightHead" style={{ backgroundColor: Colors.DataTblHeaderbg }}>
              <p className="eMed_srvConfig_rightHead_txt">Checklist Master</p>
            </div>
            <div className="eMed_srvConfig_subDiv" style={{ p: 2, maxWidth: '30vw', overflow: 'visible' }}>
              <FormControl fullWidth size="small" sx={{ m: 1 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={checklistType}
                  label="Type"
                  onChange={(e) => { setChecklistType(e.target.value); setSelectedRow(null); handleClear(); }}
                >
                  {CHECKLIST_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Category Title *"
                value={categoryTitle}
                inputProps={{ maxLength: 50 }}
                onChange={(e) => setCategoryTitle(e.target.value)}
                sx={{ m: 1 }}
              />
              <Box sx={{ m: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary">Checklist Items</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addItemRow} disabled={!editAccess} sx={{ textTransform: 'capitalize' }}>
                  Add
                </Button>
              </Box>
              <Box sx={{ maxHeight: '32vh', overflowY: 'auto', pl: 1 }}>
                {itemRows.map((row, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Item name"
                      value={row.item_name}
                      onChange={(e) => setItemRow(index, e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                    <IconButton size="small" color="error" onClick={() => removeItemRow(index)} disabled={itemRows.length <= 1}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </div>
            <Stack spacing={2} direction="row" id="eMed_srvConfig_rightBtnCon" sx={{ backgroundColor: Colors.DataTblHeaderbg }} alignItems="center" justifyContent="center">
              <Button variant="outlined" size="small" id="eMed_Config_btn" onClick={handleClear}>Clear</Button>
              <Button variant="contained" size="small" id="eMed_Config_btn" disabled={disableBtn || !editAccess} onClick={handleSave}>
                {selectedRow?.id ? 'Update' : 'Save'}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      {showSuccessResponse && <ToastMsg severity="success" msg={responseMessage} msgPop={msgClose} />}
      {showErrorResponse && <ToastMsg severity="error" msg={responseMessage} msgPop={msgClose} />}
      {deleteOpen && (
        <DeletePopup
          DeletTitle="Are you sure you want to delete this checklist category?"
          DeleteNotify="This action cannot be undone."
          deleteAlertPopupClose={() => { setDeleteOpen(false); setSelectedRow(null); }}
          removeData={handleDeleteConfirm}
        />
      )}
    </Box>
  );
}

export default withTranslation()(ChecklistMaster);
